import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import SEO from "@/components/SEO";
import { Button } from "@/components/ui/button";

export default function CAPADocumentation() {
  return (
    <>
      <SEO
        title="CAPA Documentation Support for Regulated Labs | LabComplyx"
        description="Streamline CAPA documentation with governed AI workflows, traceable investigation logic, and enforced human review — backed by the LabComplyx Governance & Trust framework for audit-ready records."
        canonical="https://labcomplyx.com/solutions/capa-documentation"
        breadcrumbs={[
          { name: "Home", url: "https://labcomplyx.com/" },
          { name: "Solutions", url: "https://labcomplyx.com/solutions" },
          { name: "CAPA Documentation", url: "https://labcomplyx.com/solutions/capa-documentation" },
        ]}
      />
      <div className="pt-24">
        <section className="pb-16 bg-background">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 max-w-2xl">
            <span className="inline-block text-xs font-semibold text-secondary bg-secondary/10 px-3 py-1 rounded-full mb-4">Solution</span>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-5">CAPA documentation that survives an audit</h1>
            <p className="text-lg text-muted-foreground leading-relaxed mb-8">
              LabComplyx helps regulated labs produce faster, more defensible CAPA records — with governed AI drafting, traceable investigation logic, and a human review checkpoint at every step.
            </p>
            <Link to="/ValidationIntake"><Button size="lg" className="gap-2">Start with a Free Gap Audit <ArrowRight className="w-4 h-4" /></Button></Link>
          </div>
        </section>

        <section className="py-16 bg-muted/30 border-y border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-2xl font-bold text-foreground mb-6">The CAPA documentation problem</h2>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6">Most CAPA records fail audits not because the corrective actions were wrong, but because the documentation is incomplete, inconsistent, or lacks traceable root-cause logic. Lean QA teams don't have time to do it right under pressure.</p>
                <ul className="space-y-2.5">
                  {["CAPAs written reactively, not systematically", "Root cause analysis that doesn't hold up under questioning", "Missing linkage between deviation, investigation, and action", "No audit trail for AI-assisted or collaborative drafting", "Version control gaps creating compliance exposure"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-destructive shrink-0 mt-1.5" />{b}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-card border border-border rounded-xl p-7">
                <h3 className="font-semibold text-foreground mb-4">How LabComplyx handles CAPA</h3>
                <ul className="space-y-2.5">
                  {["Governed AI drafting with CAPA-specific compliance guardrails", "Structured investigation logic from event to root cause to action", "Human review checkpoint before any record is finalized", "Full audit trail: who drafted, who reviewed, what changed", "E-signature ready and exportable for QMS submission"].map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                      <Check className="w-4 h-4 text-secondary shrink-0 mt-0.5" />{b}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="py-12 bg-background border-b border-border">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <p className="text-sm font-medium text-muted-foreground mb-4">Relevant for these lab types:</p>
            <div className="flex flex-wrap gap-3">
              {[["Clinical & Diagnostic Labs", "/industries/clinical-diagnostic"], ["Environmental & Testing Labs", "/industries/environmental-testing"], ["Pharma & GxP Support", "/industries/pharma-gxp"]].map(([label, href]) => (
                <Link key={href} to={href} className="text-xs font-medium px-3 py-1.5 rounded-lg border border-border bg-card hover:border-primary/40 hover:text-primary transition-colors">{label}</Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    </>
  );
}